import type { ReactNode } from 'react';

/**
 * The grey control bar that sits above every log / diagram panel: a cluster of
 * zoom and scroll icons on the left, the panel's name in a select on the right.
 */

interface Props {
  title: string;
  /** Extra controls rendered at the left of the icon cluster. */
  leading?: ReactNode;
  /** Options offered by the panel's own dropdown. */
  options?: readonly string[];
  accent?: boolean;
}

export function PanelToolbar({ title, leading, options, accent = false }: Props) {
  return (
    <div className="ptoolbar" role="toolbar" aria-label={`${title} controls`}>
      {leading}
      <ToolBtn label="Scroll to top" d="M4 13l6-6 6 6" active={accent} />
      <ToolBtn label="Scroll to bottom" d="M4 7l6 6 6-6" />
      <ToolBtn label="Page up" d="M10 16V5M5 10l5-5 5 5" />
      <ToolBtn label="Page down" d="M10 4v11M5 10l5 5 5-5" />
      <ToolBtn label="Zoom in" d="M10 5v10M5 10h10" circle />
      <ToolBtn label="Zoom out" d="M5 10h10" circle />
      <ToolBtn label="Fit to screen" d="M3 5h14v10H3z" />

      <span className="ptoolbar-spacer" />

      <button type="button" className="ptoolbar-info" title="Panel information">
        <svg viewBox="0 0 20 20" width="15" height="15" aria-hidden="true">
          <circle cx="10" cy="10" r="8.2" fill="currentColor" />
          <path d="M10 8.6v5.2" stroke="#fff" strokeWidth="1.8" strokeLinecap="round" />
          <circle cx="10" cy="6.2" r="1.05" fill="#fff" />
        </svg>
      </button>

      <select className="ptoolbar-select" defaultValue={title} aria-label="Panel">
        {(options ?? [title]).map((o) => (
          <option key={o}>{o}</option>
        ))}
      </select>

      <button type="button" className="ptoolbar-chev" title="More">
        <svg viewBox="0 0 20 20" width="15" height="15" aria-hidden="true">
          <path
            d="M4 6l6 5 6-5M4 11l6 5 6-5"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.6"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </button>

      <button type="button" className="tool" title="Full screen" aria-label="Full screen">
        <svg viewBox="0 0 20 20" width="15" height="15" aria-hidden="true">
          <path
            d="M7 3H3v4M13 3h4v4M7 17H3v-4M13 17h4v-4"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </button>
    </div>
  );
}

function ToolBtn({
  label,
  d,
  circle = false,
  active = false,
}: {
  label: string;
  d: string;
  circle?: boolean;
  active?: boolean;
}) {
  return (
    <button
      type="button"
      className={active ? 'tool tool--active' : 'tool'}
      title={label}
      aria-label={label}
    >
      <svg viewBox="0 0 20 20" width="15" height="15" aria-hidden="true">
        {circle && <circle cx="10" cy="10" r="7.2" fill="none" stroke="currentColor" strokeWidth="1.4" />}
        <path
          d={d}
          fill="none"
          stroke="currentColor"
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    </button>
  );
}
